<template>
  <b-container fluid>
    <b-row>
      <!-- En bloc : les boutons passent au-dessus du numéro de commande au lieu de le serrer. -->
      <b-col class="d-block">
        <div v-if="values.id" class="text-right">
          <b-button v-if="config.editCondition(values)" variant="outline-primary ml-10" size="lg"
            @click="openFormModal(values.id)">
            Modifier l'entête
            <i class="far fa-pencil fa-lg ml-10"></i>
          </b-button>
          <b-button v-if="config.editCondition(values)" variant="outline-primary ml-10" size="lg"
            @click="openAddProductsModal(values.id)">
            Ajouter des produits
            <i class="far fa-toolbox fa-lg ml-10"></i>
          </b-button>
          <b-button variant="outline-primary ml-10" size="lg" @click="duplicateItem()">
            Dupliquer la commande
            <i class="far fa-copy fa-lg ml-10"></i>
          </b-button>
          <b-button variant="outline-primary ml-10" size="lg" @click="$root.exportPdf(exportPdfData)">
            Exporter en PDF
            <i class="far fa-file-alt fa-lg ml-10"></i>
          </b-button>
          <b-button v-if="!isBusy &&
              rawValues.status.status_slug != 'renting-annulee' &&
              rawValues.status.status_slug != 'renting-terminee'
              " variant="outline-primary ml-10" size="lg" v-b-modal.modal-cancel>
            Annuler la demande
            <i class="far fa-times fa-lg ml-10"></i>
          </b-button>
        </div>
        <div class="d-flex">
          <backlink class="mt-20" />
          <div class="flex-grow-1">
            <h1 class="detail-title">
              Commande
              <span v-if="!isBusy" class="subtitle">#{{ values.renting_number }}</span>
              <b-badge variant="primary">{{ values.status }}</b-badge>
            </h1>
          </div>
        </div>
      </b-col>
    </b-row>
    <!-- Avant le chargement : placé entre le <p v-if> et le <b-row v-else>, il capterait le v-else. -->
    <ExchangeBanner v-if="!isBusy" context="renting" :id="$route.params.id" />
    <div v-if="isBusy" class="loader"></div>
    <p v-if="isBusy" class="text-center">Les données sont en cours de chargement...</p>
    <b-row v-else>
      <b-col>
        <div class="ibox">
          <div class="ibox-content divided">
            <!-- Récapitulatif en blocs (.detail-recap, cf. _global.scss), puis livraison (ou retrait)
                 et contacts sur une seconde ligne. -->
            <div class="detail-recap">
              <div class="detail-recap__block">
                <h3><i class="far fa-calendar-alt mr-10"></i>Dates</h3>
                <dl class="detail-list">
                  <dt>{{ labels.wished_delivery_date }}</dt>
                  <dd>{{ values.wished_delivery_date || "-" }}</dd>
                  <dt>{{ labels.end_renting_date }}</dt>
                  <dd>{{ values.end_renting_date || "-" }}</dd>
                  <dt>{{ labels.axiloc_creation_date }}</dt>
                  <dd>{{ values.axiloc_creation_date || "-" }}</dd>
                  <dt>{{ labels.shipping_date }}</dt>
                  <dd>{{ values.shipping_date || "-" }}</dd>
                </dl>
              </div>
              <div class="detail-recap__block">
                <h3>
                  <i class="far fa-building mr-10"></i>Affaire
                  <b-badge class="pastille-red ml-10" v-if="values.agency && externalAgencies.includes(values.agency.internal_number)">Affaire externe</b-badge>
                </h3>
                <dl class="detail-list">
                  <dt>{{ labels.agency_fullname }}</dt>
                  <dd>
                    <router-link
                      v-if="rawValues.agency && rawValues.agency.id && values.agency_fullname"
                      :to="{ name: 'AgencyDetail', params: { id: rawValues.agency.id } }"
                      target="_blank"
                      >{{ values.agency_fullname }}</router-link
                    >
                    <template v-else>{{ values.agency_fullname || "-" }}</template>
                  </dd>
                  <dt>{{ labels.business_fullname }}</dt>
                  <dd>
                    <template v-if="values.business">
                      <router-link
                        v-if="rawValues.business && rawValues.business.id"
                        :to="{ name: 'BusinessDetail', params: { id: rawValues.business.id } }"
                        target="_blank"
                        >{{ values.business.number }} - {{ values.business.name }}</router-link
                      >
                      <template v-else>{{ values.business.number }} - {{ values.business.name }}</template>
                    </template>
                    <template v-else>-</template>
                  </dd>
                  <dt>Facturation</dt>
                  <dd>{{ values.business ? "Affaire" : "Agence" }}</dd>
                </dl>
              </div>
              <div class="detail-recap__block">
                <h3><i class="far fa-user mr-10"></i>Demandeur</h3>
                <dl class="detail-list">
                  <template v-if="values.user">
                    <dt>{{ labels.user }}</dt>
                    <dd>{{ values.user }}</dd>
                  </template>
                  <dt>{{ labels.asker_email }}</dt>
                  <dd>
                    <a v-if="values.asker_email" :href="'mailto:' + values.asker_email">{{ values.asker_email }}</a>
                    <template v-else>-</template>
                  </dd>
                  <dt>{{ labels.asker_number }}</dt>
                  <dd>{{ values.asker_number || "-" }}</dd>
                  <dt>{{ labels.employee }}</dt>
                  <dd>{{ values.employee || "-" }}</dd>
                </dl>
              </div>
              <div class="detail-recap__block">
                <h3><i class="far fa-clipboard-list mr-10"></i>Commande</h3>
                <dl class="detail-list">
                  <dt>{{ labels.renting_type_slug }}</dt>
                  <dd>
                    <b-badge v-if="rawValues.renting_type_slug == 'exchange'" variant="info">{{ values.renting_type_slug }}</b-badge>
                    <template v-else>{{ values.renting_type_slug || "-" }}</template>
                  </dd>
                  <dt>{{ labels.days_before_shipped }}</dt>
                  <dd>{{ values.days_before_shipped ? values.days_before_shipped : "-" }}</dd>
                  <dt>{{ labels.comment }}</dt>
                  <dd class="detail-recap__comment">{{ values.comment || "-" }}</dd>
                </dl>
              </div>
            </div>
            <b-row>
              <!-- Multi-entrepôt : le client choisit son point de retrait au tunnel, il faut donc
                   dire lequel — et donner ses coordonnées à qui prépare ou renseigne. Le nom en
                   base porte déjà « Entrepôt », d'où l'intitulé qui ne le répète pas. -->
              <b-col v-if="values.withdrawed_at_agency == 1">
                <div class="renting-type-container">
                  <h3>Retrait</h3>
                </div>

                <ul v-if="withdrawalWarehouse">
                  <li>
                    <i class="far fa-warehouse"></i>
                    <strong>{{ withdrawalWarehouse.name }}</strong>
                    <span class="text-muted" v-if="withdrawalWarehouse.internal_code">
                      ({{ withdrawalWarehouse.internal_code }})
                    </span>
                  </li>
                  <li v-if="withdrawalWarehouse.address">
                    <i class="far fa-map-marker-alt"></i>
                    {{ withdrawalWarehouse.address }}
                  </li>
                  <li v-if="withdrawalWarehouse.phone">
                    <i class="far fa-phone"></i>
                    <a :href="'tel:' + withdrawalWarehouse.phone">{{ formatPhone(withdrawalWarehouse.phone) }}</a>
                  </li>
                </ul>
                <ul v-else>
                  <li class="text-muted">Entrepôt de retrait non renseigné</li>
                </ul>
              </b-col>
              <b-col v-if="values.withdrawed_at_agency != 1">
                <div class="renting-type-container">
                  <h3>Livraison</h3>
                  <span v-if="values.withdrawed_at_agency == 0 && values.renting_retrieving_place == 'agency'"
                    class="badge badge-agency">Livraison en agence</span>
                  <span v-if="values.withdrawed_at_agency == 0 && values.renting_retrieving_place == 'project'"
                    class="badge badge-project">Livraison sur un chantier</span>
                  <span v-if="values.withdrawed_at_agency == 0 && values.renting_retrieving_place == 'other'"
                    class="badge badge-other">Autre livraison</span>
                </div>

                <ul>
                  <li>{{ values.delivery_place }} : {{ values.delivery_address || "-" }}</li>
                  <li>{{ values.zip_code || "-" }} {{ values.city || "-" }}</li>
                  <li v-if="values.transport_distance_km">
                    <i class="far fa-route"></i>
                    {{ values.transport_distance_km }} km
                    <span class="text-muted" v-b-tooltip.hover :title="transportDistanceHelp">
                      ({{ transportDistanceSource }})
                    </span>
                  </li>
                  <!-- Frais de transport : montant figé à la commande, son état, et le geste
                       commercial. Le forfait couvre l'aller-retour et n'est facturé qu'une fois. -->
                  <!-- Commande de vente : le transport est inclus dans le prix de vente. -->
                  <li v-if="values.transport_included_in_price && !values.transport_fee">
                    <i class="far fa-euro-sign"></i>
                    Transport <strong>inclus dans le prix de vente</strong>
                  </li>
                  <li v-if="values.transport_fee">
                    <i class="far fa-euro-sign"></i>
                    <strong>{{ transportFeeLabel }}</strong>
                    <template v-if="values.transport_waived_at">
                      <span class="badge badge-warning ml-10">Offert</span>
                      <div class="text-muted">
                        par {{ values.transport_waived_by || "un utilisateur" }}
                        le {{ values.transport_waived_at | moment("DD-MM-YYYY") }}
                      </div>
                    </template>
                    <template v-else>
                      <span class="text-muted"> aller/retour</span>
                      <div class="text-muted" v-if="values.transport_billed_at">
                        Facturé le {{ values.transport_billed_at | moment("DD-MM-YYYY") }}
                      </div>
                      <div class="text-muted" v-else>Pas encore facturé</div>
                    </template>
                  </li>
                </ul>
                <dl>
                  <dt>{{ labels.required_truck_tailgate }}</dt>
                  <dd>{{ values.required_truck_tailgate == 1 ? "Oui" : "Non" }}</dd>
                  <dt>{{ labels.reachable_by_semitrailer_truck }}</dt>
                  <dd>{{ values.reachable_by_semitrailer_truck == 1 ? "Oui" : "Non" }}</dd>
                </dl>
              </b-col>
              <b-col v-if="values.withdrawed_at_agency != 1">
                <h3>Premier contact</h3>
                <ul>
                  <li>
                    <i class="far fa-user"></i>
                    {{ values.first_contact_name || "-" }}
                  </li>
                  <li>
                    <i class="far fa-phone"></i>
                    <a v-if="values.first_contact_phone" :href="'tel:' + values.first_contact_phone">{{
              values.first_contact_phone
            }}</a>
                    <template v-else>-</template>
                  </li>
                </ul>
                <h3>Second contact</h3>
                <ul v-if="values.second_contact_name || values.second_contact_phone">
                  <li>
                    <i class="far fa-user"></i>
                    {{ values.second_contact_name || "-" }}
                  </li>
                  <li>
                    <i class="far fa-phone"></i>
                    <a v-if="values.second_contact_phone" :href="'tel:' + values.second_contact_phone">{{
              values.second_contact_phone
            }}</a>
                    <template v-else>-</template>
                  </li>
                </ul>
                <p v-else class="text-muted">Aucune information renseignée pour le second contact.</p>
              </b-col>
            </b-row>
          </div>
        </div>
      </b-col>
    </b-row>

    <b-row v-if="!isBusy && values.stored_files && values.stored_files.length">
      <b-col>
        <div class="ibox">
          <div class="ibox-title"><i class="far fa-folder"></i>Fichiers joints</div>
          <div class="ibox-content">
            <div class="table-wrapper">
              <b-table hover :fields="[{ key: 'file', label: 'Fichier' }]" :items="values.stored_files">
                <template slot="file" slot-scope="data">
                  <a :href="BACK_FILES_URL + data.value" target="_blank">{{ data.value }}</a>
                </template>
              </b-table>
            </div>
          </div>
        </div>
      </b-col>
    </b-row>

    <b-row v-if="!isBusy && values.renting_preparations && values.renting_preparations.length">
      <b-col v-for="renting_preparation in   values.renting_preparations  " :key="renting_preparation.id">
        <div class="ibox" :class="['renting_preparation', renting_preparation.group_slug]">
          <div class="ibox-title">
            <i class="far fa-toolbox"></i>
            {{
              ($store.getters["renting_preparation/getGroup"](renting_preparation.group_slug) || {}).text || renting_preparation.group_slug
            }}
            <b-badge variant="info" class="float-right">{{
                renting_preparation.status ? renting_preparation.status.name : null
              }}</b-badge>
            <b-button :variant="renting_preparation.quantity_comment > 0 ? 'primary' : 'outline-primary'" size="sm"
              class="float-right m-r" @click="openRentingPreparationCommentsModal(renting_preparation.id)">
              Commentaires <template v-if="renting_preparation.quantity_comment > 0">({{
              renting_preparation.quantity_comment
            }})</template> <i :class="[renting_preparation.quantity_comment > 0 ? 'fa' : 'far', 'fa-comments']"></i>
            </b-button>
          </div>
          <div class="ibox-content">
            <b-row>
              <b-col>
                <dl>
                  <dt>Préparateur</dt>
                  <dd>
                    {{
              renting_preparation.preparator &&
                renting_preparation.preparator.firstname &&
                renting_preparation.preparator.lastname
                ? renting_preparation.preparator.firstname +
                " " +
                renting_preparation.preparator.lastname
                : "-"
            }}
                  </dd>
                </dl>
              </b-col>
              <b-col>
                <!-- INC-004 : entrepôt de rattachement de la préparation (bon de préparation) -->
                <dl>
                  <dt>Entrepôt</dt>
                  <dd>{{ renting_preparation.warehouse && renting_preparation.warehouse.name ? renting_preparation.warehouse.name : "-" }}</dd>
                </dl>
              </b-col>
              <b-col>
                <dl>
                  <dt>Date de préparation</dt>
                  <dd v-if="renting_preparation.preparation_date">{{ renting_preparation.preparation_date |
              moment("DD-MM-YYYY") }}</dd>
                  <dd v-else>-</dd>
                </dl>
              </b-col>
            </b-row>
            <!-- Deux actions de même nature sur la préparation, côte à côte et de même poids :
                 elles se lisent ensemble, plutôt que l'une dans le bandeau de titre et l'autre
                 sous les informations. -->
            <b-row>
              <b-col cols="6" v-if="renting_preparation.status &&
              !isPreparationFinal(renting_preparation) &&
              rawValues.status.status_slug != 'renting-annulee'
              ">
                <!-- Longue durée (SFD 2.8) : le serveur refuse d'affecter un préparateur tant
                     qu'une ligne engagée de CETTE préparation n'a pas son prix négocié. Le bouton
                     l'annonce au lieu de laisser buter sur le refus. Le survol reste actif sur
                     l'enveloppe, un bouton désactivé n'émettant pas d'événement de souris. -->
                <span class="btn-select-preparator-wrapper" v-b-tooltip.hover
                  :title="longTermPriceMissing(renting_preparation)
              ? 'Renseignez le prix journalier de la ligne en location longue durée avant d\'affecter un préparateur'
              : ''
            ">
                  <b-button block @click.prevent="openSelectPreparatorModal(
              renting_preparation.id,
              renting_preparation.group_slug
            )
              " variant="outline-primary" class="m-b btn-select-preparator"
                    :disabled="longTermPriceMissing(renting_preparation)">
                    {{
              renting_preparation.preparator
                ? "Modifier l'affectation"
                : "Affecter la préparation"
            }}
                    <i class="far fa-pencil fa-lg ml-10"></i>
                  </b-button>
                </span>
              </b-col>
              <b-col cols="6"
                v-if="config.editCondition(values) && (renting_preparation.status.status_slug == 'renting-non-prise-en-compte' || renting_preparation.status.status_slug == 'renting-preparation-en-attente-de-preparation' || renting_preparation.status.status_slug == 'renting-preparation-en-preparation')">
                <b-button block variant="outline-primary" class="m-b"
                  @click="openRentingPreparationModal(renting_preparation.id)">
                  Modifier les quantités
                  <i class="far fa-pencil fa-lg ml-10"></i>
                </b-button>
              </b-col>
            </b-row>
            <div class="table-wrapper">
              <b-table hover v-if="renting_preparation.products_renting &&
              renting_preparation.products_renting.length
              " :fields="rentingProductsFields(renting_preparation)" :items="renting_preparation.products_renting"
                :tbody-tr-class="longTermRowClass">
                <template slot="product_renting" slot-scope="data">
                  <router-link :to="{
              name: 'ProductDetail',
              params: { id: data.item.product.id },
            }">{{ data.item.product.name }}</router-link>
                  <!-- Emplacement du produit dans l'entrepôt de la préparation -->
                  <small v-if="storageLabel(data.item.product)" class="d-block text-muted">
                    <i class="far fa-map-marker-alt"></i> {{ storageLabel(data.item.product) }}
                  </small>
                </template>
                <template slot="stock" slot-scope="data">
                  <StockByWarehousePopover :products="[{ id: data.item.product.id, name: data.item.product.name }]">
                    <b-badge :variant="data.item.product.stock <= 0
              ? 'danger'
              : data.item.product.stock <= data.item.product.stock_mini
                ? 'warning'
                : 'success'
              ">{{ data.item.product.stock }}</b-badge>
                  </StockByWarehousePopover>
                </template>
                <template slot="buffer_stock" slot-scope="data">
                  <b-badge :variant="(data.item.product.stock - data.item.product.buffer_stock) <= 0
              ? 'danger'
              : (data.item.product.stock - data.item.product.buffer_stock) <= data.item.product.stock_mini
                ? 'warning'
                : 'success'
              ">{{ (data.item.product.stock - data.item.product.buffer_stock) }}</b-badge>
                </template>
                <!-- Longue durée (SFD 2.8) : l'engagement pris par le client au panier. -->
                <template slot="long_term" slot-scope="data">
                  <b-badge v-if="data.item.long_term_months" variant="info">
                    {{ data.item.long_term_months }} mois
                  </b-badge>
                  <span v-else class="text-muted">-</span>
                </template>
                <!-- Tarif négocié pour cet engagement. Il se saisit jusqu'à l'expédition de la
                     préparation qui porte la ligne ; ensuite il est figé sur les mouvements de
                     location et la facturation a commencé. -->
                <template slot="long_term_price" slot-scope="data">
                  <span v-if="!data.item.long_term_months" class="text-muted">-</span>
                  <span v-else-if="!data.item.long_term_daily_price"
                    class="long-term-price long-term-price--missing"
                    :class="{ 'long-term-price--editable': canEditLongTermPrice(renting_preparation) }"
                    :title="canEditLongTermPrice(renting_preparation) ? 'Saisir le prix journalier de cet engagement' : 'Prix jamais renseigné, et cette préparation est expédiée'"
                    @click="canEditLongTermPrice(renting_preparation) && openLongTermPriceModal(data.item)">
                    {{ canEditLongTermPrice(renting_preparation) ? "À saisir" : "Non renseigné" }}
                  </span>
                  <span v-else class="long-term-price"
                    :class="{ 'long-term-price--editable': canEditLongTermPrice(renting_preparation) }"
                    :title="canEditLongTermPrice(renting_preparation) ? 'Modifier le prix journalier' : 'Prix figé : cette préparation est expédiée'"
                    @click="canEditLongTermPrice(renting_preparation) && openLongTermPriceModal(data.item)">
                    {{ formatDailyPrice(data.item.long_term_daily_price) }}
                  </span>
                </template>
                <!-- Prix journalier négocié porté par les mouvements (locations MCS, cf.
                     app:mcs:import-location-prices) : il remplace la grille du produit. -->
                <template slot="negotiated_price" slot-scope="data">
                  <span v-if="data.item.negotiated_daily_prices && data.item.negotiated_daily_prices.length">
                    {{ data.item.negotiated_daily_prices.map(formatDailyPrice).join(" / ") }}
                  </span>
                  <span v-else class="text-muted">-</span>
                </template>
                <template v-slot:row-details="row">
                  <b-row v-for="unique_reference in row.item.unique_references" :key="unique_reference.id">
                    <b-col class="pl-4">
                      <router-link :to="{ name: 'ReferenceDetail', params: { id: unique_reference.id } }">{{
              unique_reference.internal_code
            }}</router-link>
                    </b-col>
                  </b-row>
                </template>
              </b-table>
              <b-table hover v-if="renting_preparation.products_selling.products &&
              renting_preparation.products_selling.products.length
              " :fields="products_selling_fields" :items="renting_preparation.products_selling.products">
                <template slot="product_selling" slot-scope="data">
                  <router-link :to="{ name: 'ProductDetail', params: { id: data.item.product.id } }">{{
              data.item.product.name
            }}</router-link>
                  <!-- Emplacement du produit dans l'entrepôt de la préparation -->
                  <small v-if="storageLabel(data.item.product)" class="d-block text-muted">
                    <i class="far fa-map-marker-alt"></i> {{ storageLabel(data.item.product) }}
                  </small>
                </template>
                <template slot="prepared" slot-scope="data">
                  <b-badge v-if="data.value == true" variant="success" class="circle float-left">
                    <i class="fa fa-check"></i>
                  </b-badge>
                  <b-badge v-else-if="data.value == false" variant="danger" class="circle float-left">
                    <i class="fa fa-times"></i>
                  </b-badge>
                </template>
                <template slot="stock" slot-scope="data">
                  <StockByWarehousePopover :products="[{ id: data.item.product.id, name: data.item.product.name }]">
                    <b-badge :variant="data.item.product.stock <= 0
              ? 'danger'
              : data.item.product.stock <= data.item.product.stock_mini
                ? 'warning'
                : 'success'
              ">{{ data.item.product.stock }}</b-badge>
                  </StockByWarehousePopover>
                </template>
                <template slot="buffer_stock" slot-scope="data">
                  <b-badge :variant="(data.item.product.stock - data.item.product.buffer_stock) <= 0
              ? 'danger'
              : (data.item.product.stock - data.item.product.buffer_stock) <= data.item.product.stock_mini
                ? 'warning'
                : 'success'
              ">{{ (data.item.product.stock - data.item.product.buffer_stock) }}</b-badge>
                </template>
              </b-table>
              <template v-if="renting_preparation.products_selling.lots &&
              renting_preparation.products_selling.lots != {}">
                <div v-for="(lot, index) in renting_preparation.products_selling.lots" :key="index"
                  class="lot m-sm p-sm bg-info">
                  <h3 class="text-muted m-b-sm">Lot : {{ lot.name }}</h3>
                  <b-table hover v-if="lot.products && lot.products.length" :fields="products_selling_fields"
                    :items="lot.products">
                    <template slot="product_selling" slot-scope="data">
                      <router-link :to="{ name: 'ProductDetail', params: { id: data.item.product.id } }">{{
              data.item.product.name
            }}</router-link>
                      <!-- Emplacement du produit dans l'entrepôt de la préparation -->
                      <small v-if="storageLabel(data.item.product)" class="d-block text-muted">
                        <i class="far fa-map-marker-alt"></i> {{ storageLabel(data.item.product) }}
                      </small>
                    </template>
                    <template slot="prepared" slot-scope="data">
                      <b-badge v-if="data.value == true" variant="success" class="circle float-left">
                        <i class="fa fa-check"></i>
                      </b-badge>
                      <b-badge v-else-if="data.value == false" variant="danger" class="circle float-left">
                        <i class="fa fa-times"></i>
                      </b-badge>
                    </template>
                    <template slot="stock" slot-scope="data">
                      <StockByWarehousePopover :products="[{ id: data.item.product.id, name: data.item.product.name }]">
                        <b-badge :variant="data.item.product.stock <= 0
              ? 'danger'
              : data.item.product.stock <= data.item.product.stock_mini
                ? 'warning'
                : 'success'
              ">{{ data.item.product.stock }}</b-badge>
                      </StockByWarehousePopover>
                    </template>
                  </b-table>
                </div>
              </template>
            </div>
            <b-row v-if="renting_preparation.shipping">
              <b-col class="text-center bottom-link">Expédition {{ renting_preparation.shipping.shipping_number }} :
              </b-col>
              <b-col class="text-center bottom-link">
                <router-link :to="{ name: 'ShippingDetail', params: { id: renting_preparation.shipping.id } }">
                  Voir
                  <i class="fa fa-chevron-right"></i>
                </router-link>
              </b-col>
              <b-col class="text-center bottom-link">
                <a href @click.prevent="exportShippingPdfData(renting_preparation.shipping.id)">
                  Exporter en PDF
                  <i class="fa fa-file-alt"></i>
                </a>
              </b-col>
            </b-row>
            <b-row v-if="can_prepare_on_mobile">
              <router-link :to="{
              name: 'RentingPreparationMobileDetail',
              params: { id: renting_preparation.id },
            }" target="_blank">Préparer sur mobile</router-link>
            </b-row>
          </div>
        </div>
      </b-col>
    </b-row>

    <AdminModalForm v-if="config.useNewModal || config.useEditModal" :config="config" :id_to_edit="id_to_edit" />

    <b-modal id="add_products_form" lazy hide-footer centered no-close-on-backdrop :size="config.modal_size || 'lg'">
      <template slot="modal-title">
        <i class="far fa-plus fa-lg"></i>
        Ajout de produits à la commande
      </template>
      <RentingAddProductsForm :id_to_edit="id_to_edit" />
    </b-modal>

    <b-modal id="renting_preparation_form" lazy hide-footer centered no-close-on-backdrop
      :size="config.modal_size || 'lg'">
      <template slot="modal-title">
        <i class="far fa-plus fa-lg"></i>
        Edition de la préparation
      </template>
      <RentingPreparationForm :id_to_edit="id_to_edit" />
    </b-modal>

    <b-modal id="modal-cancel" hide-footer centered no-close-on-backdrop>
      <template slot="modal-title">Annuler la demande</template>
      <div class="d-block text-center">
        <h3>Êtes-vous sur de vouloir annuler cette demande ?</h3>
      </div>
      <b-button class="mt-3" variant="success" block @click="cancel()">Confirmer</b-button>
      <b-button class="mt-3" variant="outline-secondary" block @click="cancelCancel()">Annuler</b-button>
    </b-modal>

    <!-- Longue durée (SFD 2.8) : saisie du prix journalier négocié. -->
    <b-modal id="modal-long-term-price" hide-footer centered no-close-on-backdrop>
      <template slot="modal-title">Prix journalier — location longue durée</template>
      <div v-if="longTermLine">
        <p>
          <strong>{{ longTermLine.product.name }}</strong><br />
          Engagement de <strong>{{ longTermLine.long_term_months }} mois</strong>,
          quantité {{ longTermLine.quantity }}.
        </p>
        <!-- Grille du produit : le tarif d'un engagement se négocie en regard des paliers
             qu'il remplace — sans eux, l'administrateur saisirait un prix à l'aveugle. -->
        <div class="long-term-grid mb-3">
          <div class="long-term-grid__title">Tarification catalogue</div>
          <div v-if="isLoadingLongTermPrices" class="text-muted">
            <i class="fa fa-spinner fa-spin mr-1"></i> Chargement des paliers…
          </div>
          <PriceGrids v-else-if="longTermProductPrices.length" :prices="longTermProductPrices"
            show-single-label />
          <div v-else class="text-muted">Aucun palier tarifaire renseigné pour ce produit.</div>
        </div>
        <!-- Le thème décale labels et textes d'aide de 20px pour les aligner sur le padding
             interne des champs. Dans cette modale, ils doivent suivre le bloc au-dessus :
             d'où le ml-0, ici plutôt que dans le style global qui vaut pour tout le back-office. -->
        <b-form-group label="Prix journalier fixe *" label-for="long-term-price-input"
          label-class="ml-0">
          <b-input-group append="€ / jour">
            <b-form-input id="long-term-price-input" type="number" min="0" step="0.01" autofocus
              v-model="longTermPrice" @keyup.enter="saveLongTermPrice()" />
          </b-input-group>
          <small class="form-text text-muted ml-0">
            Ce tarif remplace la grille par paliers : il s'applique dès le premier jour et pour
            toute la durée de l'engagement.
          </small>
        </b-form-group>
        <b-alert show variant="warning">
          <i class="far fa-exclamation-triangle mr-1"></i>
          Tant que la préparation n'est <strong>pas expédiée</strong>, ce prix reste modifiable.
          Une fois le matériel parti, la facturation commence : le tarif est figé et ne pourra
          plus être corrigé ici.
        </b-alert>
        <b-button class="mt-3" variant="success" block :disabled="isSavingLongTermPrice"
          @click="saveLongTermPrice()">
          <i v-if="isSavingLongTermPrice" class="fa fa-spinner fa-spin"></i>
          Enregistrer
        </b-button>
        <b-button class="mt-3" variant="outline-secondary" block
          @click="$root.$emit('bv::hide::modal', 'modal-long-term-price')">Annuler</b-button>
      </div>
    </b-modal>

    <b-modal id="modal-waive-transport" hide-footer centered no-close-on-backdrop>
      <template slot="modal-title">Offrir le transport</template>
      <div class="d-block text-center">
        <h3>Offrir les {{ transportFeeLabel }} de transport de cette commande ?</h3>
        <p class="text-muted" v-if="values.transport_billed_at">
          La ligne déjà facturée sera retirée de l'export SAP.
        </p>
      </div>
      <b-button class="mt-3" variant="success" block :disabled="isWaiving" @click="waiveTransport()">
        <i v-if="isWaiving" class="fa fa-spinner fa-spin"></i>
        Confirmer
      </b-button>
      <b-button class="mt-3" variant="outline-secondary" block @click="cancelWaiveTransport()">Annuler</b-button>
    </b-modal>

    <b-modal id="modal-comments" hide-footer centered no-close-on-backdrop>
      <template slot="modal-title">Commentaires de la préparation</template>
      <RentingPreparationComments :id_to_edit="id_to_edit" />
    </b-modal>

    <b-modal id="select_preparator_modal" lazy hide-footer centered no-close-on-backdrop>
      <template slot="modal-title">
        <i class="far fa-toolbox"></i>
        Sélectionner un préparateur
      </template>
      <b-form>
        <!-- INC-001 : seuls les utilisateurs ayant accès à tous les entrepôts peuvent réaffecter -->
        <b-form-group v-if="isAllWarehouses" label="Entrepôt" label-for="warehouse_id">
          <b-form-select class="flex-fill" id="warehouse_id" v-model="selected_warehouse_id"
            :options="warehouse_options" @change="selected_preparator_id = null">
            <option slot="first" :value="null">-- Tous les entrepôts --</option>
          </b-form-select>
        </b-form-group>
        <b-form-group label="Préparateur" label-for="preparator_id">
          <!-- Liste avec recherche : on tape le nom plutôt que de dérouler tous les préparateurs. -->
          <OptionsAutocomplete id="preparator_id" v-model="selected_preparator_id" variant="form" :limit="200"
            placeholder="Nom du préparateur…" :options="selected_preparation_group_slug == 'instruments'
              ? im_preparators
              : selected_preparation_group_slug == 'service_et_logistique'
                ? snl_preparators
                : ot_preparators
              " />
        </b-form-group>
        <b-form-group label="Date de préparation" label-for="preparation_date">
          <datepicker id="preparation_date" input-class="form-control" v-model="selected_preparation_date" required
            :format="'dd/MM/yyyy'" :monday-first="true" :language="fr" :clear-button="true"
            clear-button-icon="fa fa-times fa-lg" :disabledDates="disabled_preparation_dates">
          </datepicker>
        </b-form-group>
        <div class="form-submit">
          <b-button type="submit" variant="outline-primary"
            :disabled="!selected_preparator_id || !selected_preparation_date" @click.prevent="selectPreparator()">
            Enregistrer</b-button>
        </div>
      </b-form>
    </b-modal>
  </b-container>
</template>

<script>
import config from "./config";
import ExchangeBanner from "@/components/Exchange/ExchangeBanner";
import renting_preparation_product_config from "@/components/RentingPreparationProduct/config";
import shipping_config from "@/components/Shipping/config";
import AdminModalForm from "@/components/Global/AdminModalForm";
import RentingAddProductsForm from "@/components/RentingAddProducts/RentingAddProductsForm";
import RentingPreparationForm from "@/components/RentingPreparation/RentingPreparationForm";
import RentingPreparationComments from "@/components/RentingPreparation/RentingPreparationComments";
import StockByWarehousePopover from "@/components/Global/StockByWarehousePopover";
import PriceGrids from "@/components/Global/PriceGrids";
import { formatPhone as formatPhoneNumber } from "@/filters/phone";

import Datepicker from "vuejs-datepicker";
import { en, fr } from "vuejs-datepicker/dist/locale";
import axios from "@/axiosConnection";
import { environmentLabel } from "@/components/StorageEnvironment/labels";

// Statuts terminaux d'une préparation : miroir de RentingPreparationStatusConstant::FINAL_STATUSES
// côté back, qui refuse désormais tout changement de statut une fois l'un d'eux atteint.
const PREPARATION_FINAL_STATUSES = [
  "renting-preparation-expediee",
  "renting-preparation-annulee",
];

export default {
  name: "RentingDetail",
  components: {
    ExchangeBanner,
    AdminModalForm,
    RentingAddProductsForm,
    RentingPreparationForm,
    RentingPreparationComments,
    StockByWarehousePopover,
    Datepicker,
    PriceGrids,
  },
  data() {
    return {
      config: config,
      en: en,
      fr: fr,
      renting_preparation_product_config: renting_preparation_product_config,
      shipping_config: shipping_config,
      labels: {},
      isBusy: false,
      isWaiving: false,
      products_selling_fields: [],
      products_renting_fields: [],
      id_to_edit: null,
      selected_preparation_id: null,
      selected_preparation_group_slug: null,
      selected_preparator_id: null,
      selected_warehouse_id: null, // INC-004 : entrepôt de la préparation (réaffectation)
      selected_preparation_date: null,
      externalAgencies: process.env.EXTERNAL_AGENCIES,
      // Longue durée (SFD 2.8) : ligne en cours de tarification, montant saisi, et paliers
      // catalogue du produit affichés en regard.
      longTermLine: null,
      longTermPrice: null,
      isSavingLongTermPrice: false,
      longTermProductPrices: [],
      isLoadingLongTermPrices: false
    };
  },
  computed: {
    /**
     * On planifie une préparation : la date ne peut pas être dans le passé. `to` désactive
     * tout ce qui est strictement antérieur, aujourd'hui reste donc sélectionnable.
     */
    disabled_preparation_dates: function () {
      return { to: this.$moment().startOf("day").toDate() };
    },
    /**
     * Statuts de préparation à partir desquels le prix négocié n'est plus modifiable — mêmes
     * valeurs que `RentingPreparationProductManager::PRICE_LOCKING_PREPARATION_STATUSES`.
     */
    longTermPriceLockingStatuses: function () {
      return ["renting-preparation-expediee", "renting-preparation-annulee"];
    },
    /**
     * Entrepôt de retrait choisi par le client (INC-005). Lu sur les données brutes : `values`
     * passe par le formatage d'affichage, qui aplatit les objets imbriqués.
     */
    withdrawalWarehouse: function () {
      return this.rawValues && this.rawValues.withdrawal_warehouse
        ? this.rawValues.withdrawal_warehouse
        : null;
    },
    /**
     * Montant du transport, formaté à la française. Le forfait couvre l'aller-retour.
     */
    transportFeeLabel: function () {
      const fee = Number(this.values.transport_fee);
      if (!fee) {
        return "";
      }

      return fee.toFixed(2).replace(".", ",") + " € HT";
    },
    /**
     * Provenance de la distance retenue pour le barème transport : elle explique pourquoi
     * telle zone kilométrique a été facturée.
     */
    transportDistanceSource: function () {
      const labels = {
        address: "adresse de livraison",
        attached_warehouse: "entrepôt du département",
        fallback: "distance indéterminée",
      };
      return labels[this.values.transport_distance_source] || "source inconnue";
    },
    transportDistanceHelp: function () {
      const help = {
        address: "Itinéraire routier calculé jusqu'à l'adresse de livraison saisie.",
        attached_warehouse:
          "Adresse inexploitable : distance estimée jusqu'à l'entrepôt rattaché au département.",
        fallback:
          "Distance indéterminée (retrait, département non rattaché) : la zone maximale est appliquée.",
      };
      return help[this.values.transport_distance_source] || "";
    },
    rawValues: function () {
      return this.$store.getters[config.storeNamespace + "/getItem"](this.$route.params.id) || {};
    },
    values: function () {
      let values = this.$root.formatData(
        this.rawValues,
        this.config.fields.filter((field) => field.inDetail == true)
      );
      return {
        ...values,
        renting_preparations: values.renting_preparations
          ? values.renting_preparations
            .map((renting_preparation) => {

              // Ajout des informations du produit parent
              let products_selling_updated = {
                products: [],
                lots    : {}
              }
              renting_preparation.products_selling.map(product_selling => {
                if (product_selling.lot != null) {
                  if (products_selling_updated.lots.hasOwnProperty(product_selling.lot) !== true) {
                    products_selling_updated.lots[product_selling.lot] = {
                      product_id: product_selling.product_parent_id,
                      name      : this.$store.getters["product/getItem"](product_selling.product_parent_id) ? this.$store.getters["product/getItem"](product_selling.product_parent_id).name : null,
                      products  : []
                    }
                  }
                  products_selling_updated.lots[product_selling.lot].products.push(product_selling)
                } else {
                  products_selling_updated.products.push(product_selling)
                }
              })
              return {
                ...renting_preparation,
                products_renting: renting_preparation.products_renting
                  ? renting_preparation.products_renting.map((products_renting) => ({
                    ...products_renting,
                    _showDetails: renting_preparation.unique_references.length > 0,
                    unique_references: renting_preparation.unique_references.filter(
                      (item) => item.product.id == products_renting.product.id
                    ),
                  }))
                  : [],
                products_selling: products_selling_updated
              }
            })
            .sort((a, b) => {
              return a.group_slug == b.group_slug
                ? 0
                : a.group_slug == "service_et_logistique" ||
                  (a.group_slug == "instruments" && b.group_slug == "outillage")
                  ? 1
                  : -1;
            })
          : [],
      };
    },
    // Les trois listes ne dépendent que du droit porté, jamais du rôle : un administrateur
    // habilité est proposé au même titre qu'un préparateur.
    im_preparators: function () {
      return (
        this.$store.getters["renting_preparator/getByAuthorization"]("renting_im-preparator")
          .filter((user) => this.preparatorInSelectedWarehouse(user))
          .selectOptions("id", ["firstname", " ", "lastname"]) || []
      );
    },
    ot_preparators: function () {
      return (
        this.$store.getters["renting_preparator/getByAuthorization"]("renting_ot-preparator")
          .filter((user) => this.preparatorInSelectedWarehouse(user))
          .selectOptions("id", ["firstname", " ", "lastname"]) || []
      );
    },
    snl_preparators: function () {
      return (
        this.$store.getters["renting_preparator/getByAuthorization"]("renting_snl-preparator")
          .filter((user) => this.preparatorInSelectedWarehouse(user))
          .selectOptions("id", ["firstname", " ", "lastname"]) || []
      );
    },
    // INC-001 : true si l'utilisateur n'a pas d'entrepôt rattaché (accès à tous les entrepôts)
    isAllWarehouses: function () {
      return !this.$store.getters["auth/warehouse"];
    },
    // INC-004 : entrepôts proposés pour la réaffectation
    warehouse_options: function () {
      return this.$store.getters["warehouse/getAll"].selectOptions("id", ["internal_code", " - ", "name"]) || [];
    },
    BACK_FILES_URL: function () {
      return process.env.BACK_FILES_URL;
    },
    exportPdfData: function () {
      let formattedValues;
      let exportPdfData = {
        title: this.values[this.config.titleField],
        infos: [],
      };
      this.config.fields
        .filter((field) => field.inPdfExport == true)
        .forEach((field) => {
          exportPdfData.infos.push([
            field.label + " :",
            field.name == 'agency_fullname' ? (this.externalAgencies.some(code => this.values['agency_fullname'].includes(code)) ? [{text: 'Affaire externe', background: '#fc1938', color: '#ffffff'}, {text: this.values['agency_fullname']}] : {text: this.values['agency_fullname']}) : (
            field.name == 'renting_retrieving_place'
              ? (this.values['withdrawed_at_agency'] == 1
                ? {text: "Retrait direct à l'entrepôt ESM", background: '#519e5f'}
                : {text: this.$store.getters['renting/getRentingRetrievingPlaceBySlug'](this.values[field.name]), background: this.$store.getters['renting/getRentingRetrievingPlaceColorBySlug'](this.values[field.name])})
              : (field.type == "yesno"
                ? this.values[field.name] == true
                  ? "Oui"
                  : "Non"
                : this.values[field.name] || null))
          ]);
        });

      exportPdfData.lists = [];

      if (this.config.pdf_export_extra_content_before) {
        exportPdfData.extra_content_before = this.config.pdf_export_extra_content_before(
          this.rawValues,
          this.values,
          this
        );
      }

      if (this.config.pdf_export_extra_lists) {
        exportPdfData.lists.push(
          ...this.config.pdf_export_extra_lists(this.rawValues, this.values, this)
        );
      }

      return exportPdfData;
    },
    user_roles: function () {
      return this.$store.getters["auth/roles"] || [];
    },
    // Raccourci vers l'écran de préparation mobile : réservé aux super-admins
    // en production, ouvert à tous hors production (dev, preprod, test) pour
    // pouvoir tester la préparation mobile avec des comptes non super-admin.
    can_prepare_on_mobile: function () {
      return (
        process.env.NODE_ENV !== "production" ||
        this.user_roles.includes("ROLE_SUPER_ADMIN")
      );
    }
  },
  mounted() {

    // Chargement des champs à afficher dans le détail depuis la config
    // Et récupération des libellés
    config.fields
      .filter((field) => field.inDetail == true)
      .forEach((field) => {
        this.labels[field.name] = field.label;
      });

    this.isBusy = true;
    this.$store
      .dispatch(config.storeNamespace + "/getItem", {
        id: this.$route.params.id,
        wait: true,
      })
      .then(() => {
        // Test si le contenu demandé est bien trouvé
        let self = this;
        if (Object.entries(this.rawValues).length <= 0) {
          this.$toastr.error(
            "La fiche demandée n'a pas été trouvée. Assure-vous que l'URL saisie soit correcte."
          );
          setTimeout(function () {
            self.$router.go(-1);
          }, 5000);
          return;
        }
        this.isBusy = false;
      });

    // Préparation des champs pour les listes des produits
    var products_selling_fields = renting_preparation_product_config.fields
      .filter((field) => ["product_selling", "quantity", "stock", "buffer_stock"].includes(field.name))
      .map((field) => ({
        key: field.name,
        label: field.label,
      }));
    this.products_selling_fields = products_selling_fields;

    // Largeurs par CLÉ et non par position : le tableau gagne des colonnes au fil des
    // chantiers, et des règles en nth-of-type se décalent silencieusement à chaque ajout.
    // Les compteurs tiennent en trois chiffres ; la désignation prend ce qui reste.
    var products_renting_fields = renting_preparation_product_config.fields
      .filter((field) => ["product_renting", "quantity", "stock", "buffer_stock"].includes(field.name))
      .map((field) => ({
        key: field.name,
        label: field.label,
        class:
          field.name === "product_renting"
            ? "renting-col-product"
            : "renting-col-count",
      }));
    this.products_renting_fields = products_renting_fields;

    this.$store.dispatch("product/getAllForSelect");

  },
  methods: {
    storageLabel(product) {
      return product && product.storage_environment ? environmentLabel(product.storage_environment, false) : null;
    },
    openFormModal(id) {
      this.id_to_edit = id;
      this.$root.$emit("bv::show::modal", "admin_modal_form");
    },
    duplicateItem() {
      // Création d'une copie en retirant les champs uniques et en récupérant les id des relations
      let copy = {
        ...this.rawValues,
        agency_number: this.rawValues.agency ? this.rawValues.agency.internal_number : null,
        renting_number: this.rawValues.renting_number,
        business_number: this.rawValues.business ? this.rawValues.business.number : null,
        employee_number: this.rawValues.employee ? this.rawValues.employee.number : null,
        user_id: this.rawValues.user ? this.rawValues.user.id : null,
        status_slug: this.rawValues.status ? this.rawValues.status.status_slug : null,
        required_truck_tailgate: this.rawValues.required_truck_tailgate || false,
        reachable_by_semitrailer_truck: this.rawValues.reachable_by_semitrailer_truck || false,
        wished_delivery_date: this.rawValues.wished_delivery_date
          ? this.$moment(this.rawValues.wished_delivery_date).format("YYYY-MM-DD")
          : null,
        axiloc_creation_date: this.rawValues.axiloc_creation_date
          ? this.$moment(this.rawValues.axiloc_creation_date).format("YYYY-MM-DD")
          : null,
        products: [],
      };
      copy.id = null;

      // On stoque la copie dans le store pour la récupérer ensuite dans le formulaire
      this.$store.commit("renting/prepareCopy", copy);

      // Ouverture du formulaire de création qui sera pré-rempli
      this.openFormModal(null, "config");


    },
    openAddProductsModal(id) {
      this.id_to_edit = id;
      this.$root.$emit("bv::show::modal", "add_products_form");
    },
    openRentingPreparationModal(id) {
      this.id_to_edit = id;
      this.$root.$emit("bv::show::modal", "renting_preparation_form");
    },
    openRentingPreparationCommentsModal(id) {
      this.id_to_edit = id;
      this.$root.$emit("bv::show::modal", "modal-comments");
    },
    /**
     * Change le statut de la demande pour 'Annulée'
     */
    cancel() {
      const item = {
        ...this.rawValues,
        agency_number: this.rawValues.agency ? this.rawValues.agency.internal_number : null,
        business_number: this.rawValues.business ? this.rawValues.business.number : null,
        employee_number: this.rawValues.employee ? this.rawValues.employee.number : null,
        user_id: this.rawValues.user ? this.rawValues.user.id : null,
        withdrawed_at_agency: this.rawValues.withdrawed_at_agency
          ? this.rawValues.withdrawed_at_agency
          : false,
        status_slug: "renting-annulee",
      };

      this.$store.dispatch(this.config.storeNamespace + "/saveItem", item).then(() => {
        this.$toastr.success("La commande a été annulée");
        this.$root.$emit("bv::hide::modal", "modal-cancel");
      });
    },
    cancelCancel() {
      this.$root.$emit("bv::hide::modal", "modal-cancel");
    },
    /**
     * Geste commercial : renonce aux frais de transport de la commande. Fonctionne avant
     * comme après expédition ; si la ligne était déjà facturée, elle sort de l'export SAP.
     */
    waiveTransport() {
      this.isWaiving = true;

      axios
        .post("transport-billing/" + this.values.id + "/waive")
        .then(() => {
          this.$toastr.success("Les frais de transport ont été offerts");
          this.$root.$emit("bv::hide::modal", "modal-waive-transport");
          this.$store.dispatch(config.storeNamespace + "/getItem", {
            id: this.$route.params.id,
          });
        })
        .catch((error) => {
          const status = error.response ? error.response.status : null;
          this.$toastr.error(
            409 === status
              ? "Cette commande ne porte pas de frais de transport à offrir"
              : "Le geste commercial n'a pas pu être enregistré"
          );
        })
        .finally(() => {
          this.isWaiving = false;
        });
    },
    cancelWaiveTransport() {
      this.$root.$emit("bv::hide::modal", "modal-waive-transport");
    },
    /**
     * Montant seul : l'unité est portée par l'en-tête de colonne (« Prix / jour »), la répéter
     * sur chaque ligne allongerait la colonne sans rien apprendre. Espace insécable avant
     * l'euro, comme le veut la typographie française.
     */
    /**
     * Colonnes du tableau des locations d'une préparation.
     *
     * Les deux colonnes de longue durée (SFD 2.8) n'apparaissent que si la préparation porte
     * au moins une ligne engagée : ailleurs elles n'afficheraient que des tirets. La décision
     * se prend par préparation, une commande pouvant mêler les deux régimes. Même règle pour le
     * prix négocié des locations MCS, porté par les mouvements.
     */
    rentingProductsFields(renting_preparation) {
      const lines = renting_preparation.products_renting || [];
      const hasLongTerm = lines.some((line) => line.long_term_months);
      const hasNegotiatedPrice = lines.some(
        (line) => line.negotiated_daily_prices && line.negotiated_daily_prices.length
      );
      let fields = this.products_renting_fields;

      // Même logique que la longue durée : la colonne n'apparaît que si une ligne a un prix.
      if (hasNegotiatedPrice) {
        fields = fields.concat([
          {
            key: "negotiated_price",
            label: "Prix négocié / jour",
            class: "renting-col-long-term text-nowrap",
          },
        ]);
      }

      if (!hasLongTerm) {
        return fields;
      }

      return fields.concat([
        {
          key: "long_term",
          label: "Longue durée",
          class: "renting-col-long-term",
        },
        {
          key: "long_term_price",
          label: "Prix / jour",
          class: "renting-col-long-term text-nowrap",
        },
      ]);
    },
    /**
     * Téléphone en paires de chiffres. La règle est partagée (`@/filters/phone`) : deux écrans
     * affichent des numéros, ils ne doivent pas les découper différemment.
     */
    formatPhone(phone) {
      return formatPhoneNumber(phone);
    },
    formatDailyPrice(price) {
      return Number(price).toFixed(2).replace(".", ",") + " €";
    },
    /**
     * Longue durée (SFD 2.8) : cette préparation attend-elle encore un prix négocié ?
     *
     * Jugé préparation par préparation, comme le fait le serveur : une commande peut porter
     * plusieurs préparations dont une seule engagée, et les autres restent affectables
     * (arbitrage du 06/08/2026).
     */
    longTermPriceMissing(rentingPreparation) {
      const lines = (rentingPreparation && rentingPreparation.products_renting) || [];

      return lines.some((line) => line.long_term_months && !line.long_term_daily_price);
    },
    /**
     * Le prix journalier se négocie jusqu'à l'expédition de la préparation qui porte la ligne
     * (arbitrage du 05/08/2026) : c'est l'expédition qui fige le tarif sur les mouvements de
     * location et lance la facturation.
     *
     * Jugé sur la préparation, jamais sur la commande — deux préparations d'un même dossier
     * peuvent avoir des sorts différents, l'une expédiée et l'autre non. Reproduit la règle de
     * `RentingPreparationProductManager::isLongTermPriceLocked()`, qui refuserait de toute façon
     * l'écriture ; l'aligner ici évite d'afficher un champ saisissable que le serveur rejette,
     * ou l'inverse — un prix bloqué à l'écran alors qu'il est encore négociable.
     */
    canEditLongTermPrice(rentingPreparation) {
      const slug =
        rentingPreparation && rentingPreparation.status
          ? rentingPreparation.status.status_slug
          : null;

      return !!slug && this.longTermPriceLockingStatuses.indexOf(slug) === -1;
    },
    /**
     * Longue durée (SFD 2.8) : une ligne engagée sans tarif bloque la commande — elle ne peut
     * pas partir en préparation. La ligne est signalée en rouge pour qu'on la traite avant
     * d'aller plus loin, plutôt que de buter sur le refus à l'affectation du préparateur.
     */
    longTermRowClass(item) {
      if (item && item.long_term_months && !item.long_term_daily_price) {
        return "table-danger";
      }

      return null;
    },
    openLongTermPriceModal(line) {
      this.longTermLine = line;
      this.longTermPrice = line.long_term_daily_price;
      this.loadLongTermProductPrices(line);
      this.$root.$emit("bv::show::modal", "modal-long-term-price");
    },
    /**
     * Paliers catalogue du produit, chargés à l'ouverture : c'est la grille que le prix
     * journalier fixe va remplacer, donc la référence de la négociation. Un échec de
     * chargement n'empêche pas de saisir — la modale affiche simplement la grille en moins.
     */
    loadLongTermProductPrices(line) {
      this.longTermProductPrices = [];

      if (!line.product || !line.product.id) {
        return;
      }

      this.isLoadingLongTermPrices = true;

      axios
        .get("product/" + line.product.id)
        .then((response) => {
          this.longTermProductPrices =
            (response.data && response.data.prices) || [];
        })
        .catch(() => {
          this.longTermProductPrices = [];
        })
        .finally(() => {
          this.isLoadingLongTermPrices = false;
        });
    },
    saveLongTermPrice() {
      const price = Number(String(this.longTermPrice).replace(",", "."));

      if (!price || price <= 0) {
        this.$toastr.error("Le prix journalier doit être un montant strictement positif");
        return;
      }

      this.isSavingLongTermPrice = true;

      axios
        .patch("rentingpreparationproduct/" + this.longTermLine.id + "/long-term-price", {
          long_term_daily_price: price,
        })
        .then(() => {
          this.$toastr.success("Le prix journalier a été enregistré");
          this.$root.$emit("bv::hide::modal", "modal-long-term-price");
          this.$store.dispatch(config.storeNamespace + "/getItem", {
            id: this.$route.params.id,
          });
        })
        .catch((error) => {
          const errors =
            error.response && error.response.data && error.response.data.errors;
          this.$toastr.error(
            errors && errors.length && errors[0].message
              ? errors[0].message
              : "Le prix journalier n'a pas pu être enregistré"
          );
        })
        .finally(() => {
          this.isSavingLongTermPrice = false;
        });
    },
    /**
     * Vrai si la préparation a atteint un statut terminal : son statut ne peut plus changer, donc
     * plus question d'y réaffecter un préparateur, un entrepôt ou une date.
     */
    isPreparationFinal(renting_preparation) {
      return (
        !!renting_preparation.status &&
        PREPARATION_FINAL_STATUSES.includes(renting_preparation.status.status_slug)
      );
    },
    /**
     * Recharge la commande depuis l'API et renvoie la préparation demandée, à jour.
     * Indispensable avant de rejouer une préparation : la page peut être restée ouverte pendant
     * qu'un préparateur l'expédiait, et on renverrait alors un statut périmé.
     */
    refreshRentingPreparation(renting_preparation_id) {
      return this.$store
        .dispatch(config.storeNamespace + "/getItem", {
          id: this.$route.params.id,
          wait: true,
        })
        .then(() =>
          (this.rawValues.renting_preparations || []).find(
            (item) => item.id == renting_preparation_id
          )
        );
    },
    openSelectPreparatorModal(renting_preparation_id, renting_preparation_group_slug) {
      this.$store.dispatch("warehouse/getAll");
      // Liste unifiée des habilités à préparer : elle porte authorization_list et warehouse,
      // requis par les filtres ci-dessous. Elle ne dépend d'aucun droit sur l'annuaire.
      this.$store.dispatch("renting_preparator/getAll", true).then(() => {
        return this.refreshRentingPreparation(renting_preparation_id);
      }).then((selected_renting_preparation) => {
        if (!selected_renting_preparation) {
          this.$toastr.error("Cette préparation n'existe plus.");
          return;
        }
        if (this.isPreparationFinal(selected_renting_preparation)) {
          this.$toastr.error(
            "Cette préparation est déjà « " +
            selected_renting_preparation.status.name +
            " », son affectation ne peut plus être modifiée."
          );
          return;
        }
        this.selected_preparation_id = renting_preparation_id;
        this.selected_preparation_group_slug = renting_preparation_group_slug;
        this.selected_preparator_id = selected_renting_preparation && selected_renting_preparation.preparator
          ? selected_renting_preparation.preparator.id
          : null;
        this.selected_warehouse_id = selected_renting_preparation && selected_renting_preparation.warehouse
          ? selected_renting_preparation.warehouse.id
          : null;
        this.selected_preparation_date = selected_renting_preparation ? selected_renting_preparation.preparation_date : null;
        this.$root.$emit("bv::show::modal", "select_preparator_modal");
      })
    },
    // INC-004 : un préparateur correspond si aucun entrepôt n'est sélectionné,
    // ou s'il est rattaché à l'entrepôt sélectionné.
    preparatorInSelectedWarehouse(user) {
      if (!this.selected_warehouse_id) return true;
      // INC-001 : entrepôt unique du préparateur (null = rattaché à aucun, donc tous)
      return !user.warehouse || String(user.warehouse.id) === String(this.selected_warehouse_id);
    },
    selectPreparator() {
      this.refreshRentingPreparation(this.selected_preparation_id).then((renting_preparation) => {
        if (!renting_preparation) {
          this.$toastr.error("Cette préparation n'existe plus.");
          this.$root.$emit("bv::hide::modal", "select_preparator_modal");
          return;
        }

        // Elle a pu être expédiée ou annulée pendant que la modale était ouverte : on n'envoie
        // rien plutôt que de lui réappliquer le statut affiché, qui la ferait reculer.
        if (this.isPreparationFinal(renting_preparation)) {
          this.$toastr.error(
            "Cette préparation est passée « " +
            renting_preparation.status.name +
            " » entre-temps, la modification n'a pas été enregistrée."
          );
          this.$root.$emit("bv::hide::modal", "select_preparator_modal");
          return;
        }

        const item = {
          ...renting_preparation,
          renting_id: this.rawValues.id,
          status_slug: renting_preparation.status.status_slug,
          preparator_id: this.selected_preparator_id,
          warehouse_id: this.selected_warehouse_id, // INC-004 : réaffectation entrepôt
          preparation_date: this.selected_preparation_date,
          planning: true,
        };

        return this.$store.dispatch("renting_preparation/saveItem", item).then(() => {
          this.$root.$emit("bv::hide::modal", "select_preparator_modal");
          this.$store.dispatch(config.storeNamespace + "/getItem", {
            id: this.$route.params.id,
          });
        });
      });
    },
    exportShippingPdfData(id) {
      this.$store
        .dispatch(this.shipping_config.storeNamespace + "/getItem", {
          id: id,
          wait: true,
        })
        .then(() => {
          var item = this.$store.getters[this.shipping_config.storeNamespace + "/getItem"](id);

          let formattedItem = this.$root.formatData(
            item,
            this.shipping_config.fields.filter((field) => field.inPdfExport == true)
          );

          let exportPdfData = {
            title: this.shipping_config.pdf_export_title
              ? this.shipping_config.pdf_export_title(item, formattedItem, this)
              : item[this.shipping_config.titleField],
            link: this.shipping_config.pdf_export_link
              ? this.shipping_config.pdf_export_link(item, formattedItem, this)
              : null,
            infos: [],
          };

          if (this.shipping_config.pdf_export_logo) {
            exportPdfData.logo = this.shipping_config.pdf_export_logo;
          }

          if (this.shipping_config.pdf_export_extra_content_before) {
            exportPdfData.extra_content_before =
              this.shipping_config.pdf_export_extra_content_before(item, formattedItem, this);
          }

          if (this.shipping_config.pdf_export_custom_main_content) {
            exportPdfData.custom_main_content = this.shipping_config.pdf_export_custom_main_content(
              item,
              formattedItem,
              this
            );
          } else {
            this.shipping_config.fields
              .filter((field) => field.inPdfExport == true)
              .forEach((field) => {
                exportPdfData.infos.push([
                  field.label + " :",
                  field.type == "yesno"
                    ? formattedItem[field.name] == true
                      ? "Oui"
                      : "Non"
                    : formattedItem[field.name] || null,
                ]);
              });
          }

          let lists = this.shipping_config.fields.filter((field) => field.type == "list");
          exportPdfData.lists = [];
          if (lists && lists.length) {
            lists.forEach((list) => {
              let currentList = {
                label: list.label,
                infos: [
                  list.fields.map((field) => list.config.fields.find((f) => f.name == field).label),
                ],
              };

              if (item[list.name]) {
                this.$root
                  .formatDatas(
                    item[list.name],
                    list.config.fields.filter((field) => field.inDetail == true)
                  )
                  .forEach((item) => {
                    let infosItem = [];
                    list.fields.forEach((field) => {
                      infosItem.push(item[field] || null);
                    });
                    currentList.infos.push(infosItem);
                  });
              }

              exportPdfData.lists.push(currentList);
            });
          }

          if (this.shipping_config.pdf_export_extra_lists) {
            exportPdfData.lists.push(
              ...this.shipping_config.pdf_export_extra_lists(item, formattedItem, this)
            );
          }

          if (this.shipping_config.pdf_export_extra_content_after) {
            exportPdfData.extra_content_after = this.shipping_config.pdf_export_extra_content_after(
              item,
              formattedItem,
              this
            );
          }

          this.$root.exportPdf(exportPdfData);
        });
    },
  },
};
</script>
<style lang="scss" scoped>
@import "~@/assets/scss/_variables.scss";

/* Prix journalier d'un engagement : une donnée contractuelle, posée sur un fond pour se
   détacher de la ligne. Le badge est lui-même la commande d'édition — pas de picto à viser.
   Chiffres tabulaires pour que les montants s'alignent d'une ligne à l'autre, malgré des
   largeurs de chiffres différentes. */
.long-term-price {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 3px;
  background: $success;
  color: $primary-darker;
  font-weight: 700;
  font-size: 13px;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum";

  &--editable {
    cursor: pointer;

    &:hover {
      background: $primary;
      color: #fff;
    }
  }

  /* Tarif attendu : même pastille, registre d'alerte. La ligne entière étant déjà signalée
     en rouge, un bouton plein s'y noierait — la pastille se lit sur ce fond. */
  &--missing {
    background: $danger;
    color: #a3172b;

    &.long-term-price--editable:hover {
      background: #a3172b;
      color: #fff;
    }
  }
}

/* Paliers catalogue rappelés dans la modale de saisie : encadré discret, secondaire par
   rapport au champ de saisie qu'il éclaire. */
.long-term-grid {
  padding: 10px 12px;
  background: rgba(0, 0, 0, 0.02);
  border: 1px solid rgba(0, 0, 0, 0.08);

  &__title {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    margin-bottom: 6px;
  }
}

.renting_preparation {
  .ibox-title .btn.btn-sm {
    height: 22px;
    line-height: 22px;
    font-size: 10px;
  }

  &.outillage {

    .ibox-title,
    .ibox-title .badge {
      color: $outillage;
    }
  }

  &.instruments {

    .ibox-title,
    .ibox-title .badge {
      color: $instruments;
    }
  }

  &.service_et_logistique {

    .ibox-title,
    .ibox-title .badge {
      color: $service_et_logistique;
    }
  }

  .btn-select-preparator {
    width: 100%;
    position: relative;

    i {
      position: absolute;
      right: 20px;
      top: 50%;
      transform: translateY(-50%);
    }

    // Désactivé (prix d'engagement manquant) : le `outline-primary` du thème garde sa teinte
    // verte en opacité réduite, ce qui se lit comme un état actif. Un gris franc lève
    // l'ambiguïté.
    &:disabled,
    &.disabled {
      background-color: #f3f3f4;
      border-color: #e7eaec;
      color: #a7adb3;
      opacity: 1;
      cursor: not-allowed;
    }
  }

  // L'enveloppe qui porte l'info-bulle doit se comporter comme le bouton pleine largeur
  // qu'elle contient : un <span> reste en ligne par défaut et casserait la mise en page.
  .btn-select-preparator-wrapper {
    display: block;
  }
}

.badge {
  &.badge-axiloc {
    background-color: $primary;
    color: #ffffff;
  }

  &.badge-agency {
    background-color: $blue;
    color: #ffffff;
  }

  &.badge-project {
    background-color: $orange;
    color: #ffffff;
  }

  &.badge-other {
    background-color: $purple;
    color: #ffffff;
  }

  &.pastille-red{
    background-color: $red;
    color: #ffffff;
  }
}

.renting-type-container {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}
</style>
<style lang="scss">
.renting_preparation {
  table {
    th:nth-of-type(2) {
      width: 150px !important;
    }

    th:nth-of-type(3) {
      width: 150px !important;
    }

  }

  /* Tableau des locations : largeurs par clé de colonne, qui survivent aux ajouts de
     colonnes — contrairement aux règles en nth-of-type ci-dessus, qui visent la position et
     s'appliquent encore au tableau des ventes. Les compteurs n'ont besoin que de la place
     de trois chiffres ; la désignation, elle, se fait tronquer sans largeur suffisante. */
  table th.renting-col-count,
  table td.renting-col-count {
    width: 90px !important;
  }

  table th.renting-col-long-term,
  table td.renting-col-long-term {
    width: 115px !important;
  }

  table th.renting-col-product,
  table td.renting-col-product {
    width: auto !important;
    min-width: 240px;
  }

  .lot table {

    th:first-child,
    td:first-child {
      padding-left: 0 !important;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Renting/RentingDetail.vue